import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ClientShell from "./ClientShell";
import { useMe } from "@/lib/rehearsalRoomQueries";
import {
  useSessionsOverview, useProposeSession, useReconcileSessions, useCancelSession, useMarkNoShow,
  useCreateSessionRoomLaunch, useAbandonProposal, useAcceptTimeProposal, useDeclineTimeProposal,
} from "@/lib/sessionQueries";
import { SessionCard, formatDualTime } from "./session/SessionCard";
import { TidyCalEmbed } from "./session/TidyCalEmbed";
import FeatureOnboarding from "./onboarding/FeatureOnboarding";
import TimeProposalComposer from "./coach/TimeProposalComposer";
import CompleteSessionWizard from "./coach/CompleteSessionWizard";

const TABS = [
  { key: "booked", label: "Booked" },
  { key: "completed", label: "Completed" },
  { key: "available", label: "Available" },
];

/**
 * Master v5 Prompt 10 — the client-facing Sessions page: Completed /
 * Booked / Available. Reconciles on page open (page_open trigger, via
 * the overview query itself server-side) and again right after a booking
 * widget is closed (post_booking) — the widget's own "thank you" screen
 * is never trusted as proof a booking exists (see PRD "do not infer
 * success from iframe UI alone").
 */
export default function SessionsPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isCoach = me?.portal_role === "coach_admin";
  const { data, isPending, isError } = useSessionsOverview(workspaceId);
  const proposeSession = useProposeSession(workspaceId);
  const reconcile = useReconcileSessions(workspaceId);
  const cancelSession = useCancelSession(workspaceId);
  const markNoShow = useMarkNoShow(workspaceId);
  const createRoomLaunch = useCreateSessionRoomLaunch(workspaceId);
  const abandonProposal = useAbandonProposal(workspaceId);

  const [tab, setTab] = useState("booked");
  const [proposal, setProposal] = useState(null);
  const [joiningId, setJoiningId] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const acceptProposal = useAcceptTimeProposal(workspaceId);
  const declineProposal = useDeclineTimeProposal(workspaceId);

  const available = data?.available || {};
  const hasOpenProposal = !!available.open_proposal;
  const timeProposals = data?.time_proposals || [];

  async function handleAcceptSlot(stpId, slotId) {
    try {
      await acceptProposal.mutateAsync({ stpId, slotId });
      toast.success("Time held — your coach will confirm it on the calendar shortly");
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't accept this time");
    }
  }
  async function handleDeclineProposal(stpId) {
    try {
      await declineProposal.mutateAsync(stpId);
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't decline this proposal");
    }
  }

  async function handlePropose() {
    const res = await proposeSession.mutateAsync({ booking_mode: "single" });
    setProposal({ proposal: res.proposal, embed: res.embed });
  }

  async function handleAbandon() {
    setProposal(null);
    await abandonProposal.mutateAsync();
  }

  // Best-effort: once the member returns focus to this tab (likely
  // having just finished the embedded TidyCal checkout), re-run
  // reconciliation immediately rather than waiting for the next
  // scheduled sweep — the RESULT of this call is what confirms success,
  // never the widget's own UI.
  useEffect(() => {
    function onFocus() {
      reconcile.mutate("post_booking");
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  async function handleJoin(sesId) {
    setJoiningId(sesId);
    try {
      const res = await createRoomLaunch.mutateAsync(sesId);
      navigate(`/rehearsal-room/w/${workspaceId}/room/${res.launch_id}`);
    } finally {
      setJoiningId(null);
    }
  }

  async function handleCancel(sesId) {
    if (!window.confirm("Cancel this Session? This also cancels the booking with your coach.")) return;
    await cancelSession.mutateAsync({ sesId, reason: "Cancelled from Sessions page" });
  }

  const booked = data?.booked || [];
  const completed = data?.completed || [];
  const pending = data?.pending || [];

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="sessions-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-serif text-3xl text-ink flex items-center gap-2"><CalendarClock size={26} /> Sessions</h1>
        </div>

        <FeatureOnboarding
          workspaceId={workspaceId}
          featureKey="sessions"
          title="Welcome to Sessions"
          body="Book, join and review your coaching sessions here. Booked sessions unlock a Join button the moment your window opens, and every completed session keeps its recap and resources."
        />

        {timeProposals.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="sessions-page-time-proposals-list">
            <h2 className="text-sm font-medium text-ink-soft">Pending time proposals</h2>
            {timeProposals.map((p) => (
              <div key={p.stp_id} className="bg-mustard/10 border border-mustard/30 rounded-xl p-4" data-testid={`time-proposal-card-${p.stp_id}`}>
                <p className="text-sm text-ink mb-2">{p.reschedule_of_session_id ? "Reschedule proposal" : (isCoach ? "You proposed a time" : "Your coach proposed a time")}{p.note ? ` — ${p.note}` : ""}</p>
                <div className="flex flex-wrap gap-2">
                  {p.slots.map((slot) => {
                    const { primary } = formatDualTime(slot.starts_at, "America/Sao_Paulo");
                    return (
                      <button
                        key={slot.slot_id}
                        onClick={() => isCoach ? null : handleAcceptSlot(p.stp_id, slot.slot_id)}
                        disabled={isCoach || acceptProposal.isPending}
                        className="text-xs font-medium bg-white border border-ink/15 rounded-full px-3 py-1.5 hover:bg-terracotta/10 disabled:opacity-60"
                        data-testid={`time-proposal-slot-accept-${slot.slot_id}`}
                      >
                        {primary}
                      </button>
                    );
                  })}
                </div>
                {!isCoach && (
                  <button onClick={() => handleDeclineProposal(p.stp_id)} disabled={declineProposal.isPending} className="text-xs text-ink/50 hover:text-terracotta underline mt-2" data-testid={`time-proposal-decline-${p.stp_id}`}>
                    Decline all
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1 mb-6 border-b border-ink/10" data-testid="sessions-page-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-terracotta text-ink" : "border-transparent text-ink/50 hover:text-ink"
              }`}
              data-testid={`sessions-page-tab-${t.key}`}
            >
              {t.label} {t.key === "booked" && booked.length + pending.length > 0 && `(${booked.length + pending.length})`}
            </button>
          ))}
        </div>

        {isPending && <p className="text-ink-soft text-sm" data-testid="sessions-page-loading">Loading sessions…</p>}
        {isError && <p className="text-terracotta text-sm" data-testid="sessions-page-error">Couldn't load your sessions right now.</p>}

        {!isPending && tab === "booked" && (
          <div className="space-y-4" data-testid="sessions-page-booked-list">
            {booked.length + pending.length === 0 && <p className="text-ink-soft text-sm" data-testid="sessions-page-booked-empty">No sessions booked yet — check the Available tab.</p>}
            {pending.map((s) => (
              <SessionCard
                key={s.ses_id} session={s} isCoach={isCoach} onJoin={handleJoin} onCancel={handleCancel} workspaceId={workspaceId}
                onMarkNoShow={() => {}} onReschedule={setRescheduleTarget} viewerTimezone={me?.timezone}
                joining={joiningId === s.ses_id} cancelling={cancelSession.isPending}
              />
            ))}
            {booked.map((s) => (
              <SessionCard
                key={s.ses_id} session={s} isCoach={isCoach} onJoin={handleJoin} onCancel={handleCancel} workspaceId={workspaceId}
                onMarkNoShow={(id) => markNoShow.mutate(id)} onReschedule={setRescheduleTarget} onComplete={setCompleteTarget}
                joining={joiningId === s.ses_id} cancelling={cancelSession.isPending} viewerTimezone={me?.timezone}
              />
            ))}
          </div>
        )}

        {!isPending && tab === "completed" && (
          <div className="space-y-4" data-testid="sessions-page-completed-list">
            {completed.length === 0 && <p className="text-ink-soft text-sm" data-testid="sessions-page-completed-empty">Nothing here yet.</p>}
            {completed.map((s) => (
              <SessionCard key={s.ses_id} session={s} isCoach={isCoach} onJoin={handleJoin} onCancel={handleCancel} onMarkNoShow={() => {}} onComplete={setCompleteTarget} workspaceId={workspaceId} viewerTimezone={me?.timezone} />
            ))}
          </div>
        )}

        {!isPending && tab === "available" && !isCoach && (
          <div className="space-y-5" data-testid="sessions-page-available">
            <div className="bg-white/60 rounded-xl border border-ink/10 p-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-ink-soft">Session credits available</p>
                <p className="font-serif text-2xl text-ink" data-testid="sessions-page-balance">{available.balance ?? 0}</p>
              </div>
              {!proposal?.embed && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handlePropose}
                    disabled={proposeSession.isPending || (!available.can_propose && !hasOpenProposal)}
                    className="flex items-center gap-1.5 bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
                    data-testid="sessions-page-propose-btn"
                  >
                    {proposeSession.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {hasOpenProposal ? "Continue booking" : "Book a session"}
                  </button>
                  {hasOpenProposal && (
                    <button
                      onClick={handleAbandon}
                      disabled={abandonProposal.isPending}
                      className="text-xs text-ink/50 hover:text-terracotta underline"
                      data-testid="sessions-page-abandon-inline-btn"
                    >
                      Abandon
                    </button>
                  )}
                </div>
              )}
            </div>
            {!available.can_propose && !hasOpenProposal && (
              <p className="text-xs text-ink/45" data-testid="sessions-page-no-credits">Ask your coach for more session credits to book a new session.</p>
            )}
            {proposal?.embed && (
              <div data-testid="sessions-page-booking-widget">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-ink-soft">Pick a time below — this confirms automatically once you finish booking.</p>
                  <button
                    onClick={handleAbandon}
                    disabled={abandonProposal.isPending}
                    className="text-xs text-ink/50 hover:text-terracotta underline shrink-0"
                    data-testid="sessions-page-abandon-btn"
                  >
                    Changed your mind? Abandon this booking
                  </button>
                </div>
                <TidyCalEmbed embed={proposal.embed} />
              </div>
            )}
          </div>
        )}

        {!isPending && tab === "available" && isCoach && (
          <p className="text-ink-soft text-sm" data-testid="sessions-page-coach-available-note">
            Manage session credits for each client from the Entitlement panel (coming soon in the Commercial Center).
          </p>
        )}
      </div>
      {rescheduleTarget && (
        <TimeProposalComposer
          workspaceId={workspaceId} ownerMemberId={rescheduleTarget.owner_member_id} rescheduleOfSessionId={rescheduleTarget.ses_id}
          onClose={() => setRescheduleTarget(null)}
        />
      )}
      {completeTarget && (
        <CompleteSessionWizard
          workspaceId={workspaceId} session={completeTarget} accessType={data?.access_type}
          onClose={() => setCompleteTarget(null)}
        />
      )}
    </ClientShell>
  );
}
